"use client";

/**
 * P6-18 · Storefront popup engine. Watches this page's already-resolved
 * eligible popups for each one's trigger — delay, scroll depth, or
 * exit-intent (desktop mouseleave; mobile fast scroll-up near the top or an
 * inactivity timeout). The first satisfied, not-yet-seen popup opens; a
 * localStorage flag caps re-shows within the browser (the server caps across
 * sessions).
 *
 * P17A-10 · The eligible list used to be fetched here, client-side, on every
 * pathname change — now it arrives as `initialPopups`, resolved server-side
 * by `PopupSlot` (rendered once per navigation from `template.tsx`; see its
 * doc for why not the layout). This component owns only what a server render
 * genuinely can't: the trigger watchers below, and the dismiss/seen state in
 * localStorage.
 */
import * as React from "react";

import {
  shouldTrigger,
  type TriggerState,
} from "~/lib/marketing/popup-eligibility";
import type { RouterOutputs } from "~/trpc/react";
import { api } from "~/trpc/react";
import { PopupModal } from "./PopupModal";

type Eligible = RouterOutputs["marketing"]["activePopups"][number];

const seenKey = (id: string) => `vxpopup:${id}`;
function alreadySeen(id: string): boolean {
  try {
    return localStorage.getItem(seenKey(id)) != null;
  } catch {
    return false;
  }
}
function markSeen(id: string): void {
  try {
    localStorage.setItem(seenKey(id), "1");
  } catch {
    /* private mode — server frequency still caps */
  }
}

export function PopupHost({ path }: { path: string }) {
  // `isMobile` drives both the trigger mechanics (desktop mouseleave vs.
  // mobile scroll-up/idle) and, since P18-03, the device half of eligibility:
  // `matchMedia` in the browser rather than a User-Agent sniff in the
  // document, so the HTML is the same for every device.
  const isMobile = React.useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(pointer: coarse)").matches,
    [],
  );
  // P18-03 · The personal half of eligibility (new/returning, frequency cap,
  // the `vx_popup` cookie) is fetched from the browser on its own request —
  // one that P17A-06 never marks public and that may set its own cookie. The
  // server rendered this host only because a candidate exists for the path,
  // so a page without one still makes no request.
  const eligibility = api.marketing.activePopups.useQuery(
    { path, isMobile },
    { staleTime: Infinity, refetchOnWindowFocus: false },
  );
  const initialPopups = React.useMemo<Eligible[]>(
    () => eligibility.data ?? [],
    [eligibility.data],
  );
  const record = api.marketing.recordImpression.useMutation();
  const [active, setActive] = React.useState<Eligible | null>(null);
  const shownRef = React.useRef(false);

  const candidates = React.useMemo(
    () => initialPopups.filter((p) => !alreadySeen(p.id)),
    [initialPopups],
  );

  React.useEffect(() => {
    if (candidates.length === 0 || shownRef.current) return;

    const startedAt = Date.now();
    const state: TriggerState = {
      elapsedMs: 0,
      scrollPct: 0,
      exitSignal: false,
    };

    const open = (p: Eligible) => {
      shownRef.current = true;
      markSeen(p.id);
      setActive(p);
      record.mutate({ popupId: p.id, outcome: "SHOWN" });
    };
    const check = () => {
      if (shownRef.current) return;
      state.elapsedMs = Date.now() - startedAt;
      const winner = candidates.find((p) => shouldTrigger(p.trigger, state));
      if (winner) open(winner);
    };

    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      state.scrollPct =
        max > 0 ? Math.min(100, Math.round((doc.scrollTop / max) * 100)) : 0;
      check();
    };
    const onMouseOut = (e: MouseEvent) => {
      if (!isMobile && e.clientY <= 0) {
        state.exitSignal = true;
        check();
      }
    };
    // Mobile exit-intent: a fast scroll-up near the top, or inactivity.
    let lastY = window.scrollY;
    const onScrollMobile = () => {
      const y = window.scrollY;
      if (y < lastY - 40 && y < 200) {
        state.exitSignal = true;
        check();
      }
      lastY = y;
    };
    let idle: ReturnType<typeof setTimeout> | undefined;
    const resetIdle = () => {
      if (!isMobile) return;
      if (idle) clearTimeout(idle);
      idle = setTimeout(() => {
        state.exitSignal = true;
        check();
      }, 25_000);
    };

    const interval = setInterval(check, 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("mouseout", onMouseOut);
    if (isMobile) {
      window.addEventListener("scroll", onScrollMobile, { passive: true });
      window.addEventListener("touchstart", resetIdle, { passive: true });
      resetIdle();
    }
    check();

    return () => {
      clearInterval(interval);
      if (idle) clearTimeout(idle);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScrollMobile);
      window.removeEventListener("touchstart", resetIdle);
    };
  }, [candidates, isMobile, record]);

  if (!active) return null;
  return (
    <PopupModal
      popup={active}
      onClose={(converted) => {
        if (!converted) {
          record.mutate({ popupId: active.id, outcome: "DISMISSED" });
        }
        setActive(null);
      }}
    />
  );
}
